import React from "react";
import { Loader } from "lucide-react";
import { ChatMessagesArea } from "./ChatMessagesArea";
import ChatInputWithGallery from "./ChatInputWithGallery";
import type { ChatHistoryResponse } from "@/types/discuss.types";

const CompassDialog = React.lazy(() => import("./CompassDialog"));

interface DiscussContentViewProps {
  isHistoryLoading: boolean;
  historyError: unknown;
  historyData: ChatHistoryResponse | undefined;
  isSendingMessage: boolean;
  isCompassProcessing: boolean;
  activeProcessingTask: { prompt?: string } | null | undefined;
  welcomeMessage: string;
  lastMessageRef: React.RefObject<HTMLDivElement>;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  doc: { _id?: string; title?: string } | undefined;
  showCompassDialog: boolean;
  setShowCompassDialog: (show: boolean) => void;
  onSendMessage: (message: string) => Promise<void>;
  onRelatedQuestionClick: (question: string) => void;
}

export const DiscussContentView: React.FC<DiscussContentViewProps> = ({
  isHistoryLoading,
  historyError,
  historyData,
  isSendingMessage,
  isCompassProcessing,
  activeProcessingTask,
  welcomeMessage,
  lastMessageRef,
  scrollContainerRef,
  doc,
  showCompassDialog,
  setShowCompassDialog,
  onSendMessage,
  onRelatedQuestionClick,
}) => (
  <div className="flex flex-col flex-1 min-h-0">
    {isHistoryLoading && (
      <div className="flex justify-center items-center h-[200px]">
        <Loader className="animate-spin" />
        <span className="ml-2">Loading chat history...</span>
      </div>
    )}
    {historyError != null && (
      <div className="p-4 text-center text-sm text-danger-200">
        <p>Failed to load chat history. Please try again.</p>
      </div>
    )}
    {!isHistoryLoading && historyError == null && (
      <ChatMessagesArea
        historyData={historyData}
        isSendingMessage={isSendingMessage}
        isCompassProcessing={isCompassProcessing}
        activeProcessingTask={activeProcessingTask}
        welcomeMessage={welcomeMessage}
        lastMessageRef={lastMessageRef}
        scrollContainerRef={scrollContainerRef}
        doc={doc}
        onRelatedQuestionClick={onRelatedQuestionClick}
      />
    )}
    <ChatInputWithGallery
      onSendMessage={onSendMessage}
      isSendingMessage={isSendingMessage || isCompassProcessing}
      showCompassButton={true}
      handleCompassClick={() => setShowCompassDialog(true)}
      compassAssetSrc="../../assets/compass.png"
      requestType="discuss"
    />
    <React.Suspense fallback={null}>
      <CompassDialog isOpen={showCompassDialog} onClose={() => setShowCompassDialog(false)} />
    </React.Suspense>
  </div>
);
